import { useState, useEffect } from "react";
import { Toaster } from "sonner";
import { Routes, Route } from "react-router-dom";

import { useAuth } from "./hooks/useAuth";
import { useSubdomain } from "./hooks/useSubdomain";
import LoginPage from "./views/LoginPage";
import LandingPage from "./views/LandingPage";
import AppRouter from "./AppRouter";
import SetupWizard from "./views/setup/SetupWizard";
import SchoolWebsite from './views/schoolwebsite/SchoolWebsite';
import { subscribeStudentOnLogin } from "./utils/pushNotifications";
import IOSInstallBanner from "./components/IOSInstallBanner";

export default function AppIndex() {
  const [lang, setLang] = useState("en");
  const { user, logout, sendOTP, verifyOTP, requiresSetup, authReady } = useAuth();
  const subdomain = useSubdomain();

  useEffect(() => {
    if (user && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const timer = setTimeout(() => {
        subscribeStudentOnLogin(user);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    if (user && isAdmin && requiresSetup && window.location.pathname !== '/setup-wizard') {
      window.location.href = '/setup-wizard';
    }
  }, [user, requiresSetup]);

  // Block rendering until we know whether the stored token is valid.
  // This prevents the flash of login/app before auth resolves.
  if (!authReady) {
    return (
      <div
        style={{
          position: "fixed", inset: 0, display: "flex", alignItems: "center",
          justifyContent: "center", flexDirection: "column", gap: 20,
          background: "linear-gradient(160deg, #0A0F1E 0%, #0D1B36 50%, #0E1628 100%)",
        }}
      >
        <div style={{ width: 72, height: 72, borderRadius: 18, overflow: "hidden", boxShadow: "0 12px 40px rgba(79,70,229,0.4)" }}>
          <img src="/logo192.png" alt="EduPulseAI" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <div style={{
          width: 160, height: 2, borderRadius: 4, background: "rgba(255,255,255,0.08)",
          overflow: "hidden", position: "relative",
        }}>
          <div style={{
            position: "absolute", left: 0, top: 0, height: "100%", width: "40%", borderRadius: 4,
            background: "linear-gradient(90deg, #4F46E5, #E8690A)",
            animation: "slideBar 1.2s ease-in-out infinite",
          }} />
        </div>
        <style>{`@keyframes slideBar{0%{left:-40%}100%{left:100%}}`}</style>
      </div>
    );
  }

  const isSetupWizard = window.location.pathname === '/setup-wizard';

  const loginPage = (
    <LoginPage
      onSendOTP={sendOTP}
      onVerifyOTP={verifyOTP}
      lang={lang}
      setLang={setLang}
      subdomain={subdomain}
    />
  );

  const appRouter = isSetupWizard ? <SetupWizard /> : <AppRouter onLogout={logout} />;

  return (
    <>
      <Toaster position="top-right" richColors closeButton />
      <IOSInstallBanner />
      <Routes>
        <Route path="/s/:slug" element={<SchoolWebsite />} />
        <Route path="/" element={user ? appRouter : <LandingPage />} />
        <Route path="/login" element={user ? appRouter : loginPage} />
        <Route path="*" element={user ? appRouter : <LandingPage />} />
      </Routes>
    </>
  );
}